import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';

import Area from '../shared/Area';
import {
  FooterCompanyInfo,
  FooterCopyright,
} from '../shared/FooterCompanyInfo';
import { FooterNav } from '../shared/FooterNav';
import FooterVisualBand from '../shared/FooterVisualBand';
import type { FooterColor, FooterTone } from '../shared/footerTheme';

type Props = {
  color?: FooterColor;
};

/**
 * 원본 Emotion 은 부모의 color prop 으로 자식 셀렉터를 덮어썼다.
 * Tailwind 로 옮기면서 자식별 클래스 맵으로 편다.
 * FooterBottom 의 border-top-color 는 border-width 가 없어 실제로 렌더되지 않지만,
 * 동작을 바꾸지 않기 위해 그대로 옮긴다.
 */
const THEME: Record<FooterColor, FooterTone> = {
  white: {
    container: 'bg-apps-coreGrayWhite',
    label: 'text-apps-coreGray02',
    value: 'text-apps-coreGrayBlack',
    bottom: 'border-t-apps-coreProductDivider',
    terms: 'text-apps-coreGray02',
    termsBold: 'text-apps-coreGrayBlack',
  },
  black: {
    container: 'bg-apps-coreGrayBlack',
    label: 'text-apps-coreGray03',
    value: 'text-apps-brandGray950',
    bottom: 'border-t-apps-coreGray01',
    terms: 'text-apps-coreGray03',
    termsBold: 'text-apps-brandGray950',
  },
};

const TERMS_ITEM = 'text-sm leading-[140%] tracking-[-0.28px]';

export default function Footer({ color = 'black' }: Props) {
  const { t } = useTranslation('common');
  const tone = THEME[color];

  return (
    <div className={`flex flex-col pb-10 ${tone.container}`}>
      <FooterVisualBand variant="desktop" />
      <Area>
        <div className="pb-10 pt-20">
          <div className="flex flex-wrap items-start justify-between gap-x-16 gap-y-10">
            <FooterCompanyInfo tone={tone} variant="desktop" />
            <FooterNav color={color} variant="desktop" />
          </div>
        </div>
        <div className={`flex items-center justify-between ${tone.bottom}`}>
          <FooterCopyright variant="desktop" />
          <div className="flex gap-6">
            <div className={`${TERMS_ITEM} ${tone.termsBold}`}>
              <Link href="/terms/privacy" prefetch={false}>
                {t('개인정보처리방침')}
              </Link>
            </div>
            <div className={`${TERMS_ITEM} ${tone.terms}`}>
              <Link href="/terms/term" prefetch={false}>
                {t('이용약관')}
              </Link>
            </div>
            <div className={`${TERMS_ITEM} ${tone.terms}`}>
              <Link href="/terms/cookie" prefetch={false}>
                {t('쿠키정책')}
              </Link>
            </div>
          </div>
        </div>
      </Area>
    </div>
  );
}
